import React, { useEffect, useState } from 'react';
import type { EvaluationPlanAttachment, EvaluationPlanAttachmentSlot } from '../../types/evaluationPlan';
import {
  EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS,
  getEvaluationPlanAttachmentRegisterMethod,
} from '../../types/evaluationPlan';
import {
  guessFileNameFromShareUrl,
  normalizeShareLinkUrl,
} from '../../utils/evaluationPlanAttachment';

export type EvaluationPlanAttachmentRegisterStep = 'choose' | 'share-link' | 'web-document';
export type EvaluationPlanAttachmentRegisterMode = 'register' | 'edit';

const EvaluationPlanAttachmentRegisterModal: React.FC<{
  open: boolean;
  slot: EvaluationPlanAttachmentSlot | null;
  step: EvaluationPlanAttachmentRegisterStep;
  mode?: EvaluationPlanAttachmentRegisterMode;
  existingAttachment?: EvaluationPlanAttachment | null;
  initialShareUrl?: string;
  onClose: () => void;
  onStepChange: (step: EvaluationPlanAttachmentRegisterStep) => void;
  onOpenWebDocument: (slot: EvaluationPlanAttachmentSlot) => void;
  onSaveShareLink: (slot: EvaluationPlanAttachmentSlot, attachment: EvaluationPlanAttachment) => void;
}> = ({
  open,
  slot,
  step,
  mode = 'register',
  existingAttachment = null,
  initialShareUrl = '',
  onClose,
  onStepChange,
  onOpenWebDocument,
  onSaveShareLink,
}) => {
  const [shareUrlDraft, setShareUrlDraft] = useState('');
  const [shareUrlError, setShareUrlError] = useState<string | null>(null);
  const [optionNotice, setOptionNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setShareUrlDraft(initialShareUrl);
    setShareUrlError(null);
    setOptionNotice(null);
  }, [open, initialShareUrl, step, slot]);

  if (!open || !slot) return null;

  const slotLabel = EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[slot];
  const title =
    step === 'choose'
      ? `${slotLabel} 등록`
      : mode === 'edit'
        ? `${slotLabel} 공유링크 수정`
        : `${slotLabel} 공유링크 업로드`;

  const registeredMethod = getEvaluationPlanAttachmentRegisterMethod(existingAttachment);
  const shareLinkDisabled = registeredMethod === 'web_document';
  const webDocumentDisabled = registeredMethod === 'share_link';

  const handleSaveShareLink = () => {
    const normalized = normalizeShareLinkUrl(shareUrlDraft);
    if (!normalized) {
      setShareUrlError('올바른 http(s) 공유링크를 입력해 주세요.');
      return;
    }
    onSaveShareLink(slot, {
      kind: 'share_link',
      slot,
      registered_at: existingAttachment?.registered_at ?? new Date().toISOString(),
      preview_label: slotLabel,
      share_url: normalized,
      file_name: guessFileNameFromShareUrl(normalized),
    });
    onClose();
  };

  const handleLockedOptionClick = (message: string) => {
    setOptionNotice(message);
  };

  return (
    <div
      className="cp-modal-backdrop cp-evaluation-plan-attachment-register-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="cp-modal cp-evaluation-plan-attachment-register-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-evaluation-plan-attachment-register-title"
        data-tutorial-ep="attach-register-modal"
        data-tutorial-ep-slot={slot}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cp-evaluation-plan-attachment-register-title">{title}</h3>
        <div className="cp-modal-body cp-evaluation-plan-attachment-register-body">
          {step === 'choose' ? (
            <>
              <p className="cp-evaluation-plan-attachment-register-lead" role="status">
                등록 방식을 선택해 주세요. 한 가지 방식으로만 등록할 수 있습니다.
              </p>
              {optionNotice ? (
                <p className="cp-evaluation-plan-attachment-register-notice" role="status">
                  {optionNotice}
                </p>
              ) : null}
              <div
                className="cp-evaluation-plan-attachment-register-options"
                role="group"
                aria-label="등록 방식"
                data-tutorial-ep="attach-options"
              >
                <button
                  type="button"
                  className={`cp-evaluation-plan-attachment-register-option${webDocumentDisabled ? ' is-disabled' : ''}`}
                  data-tutorial-ep="attach-option-web-doc"
                  aria-disabled={webDocumentDisabled ? 'true' : undefined}
                  onClick={() => {
                    if (webDocumentDisabled) {
                      handleLockedOptionClick(
                        '공유링크로 등록되어 있습니다. 웹 문서 생성으로 변경하려면 등록을 삭제한 뒤 다시 등록해 주세요.'
                      );
                      return;
                    }
                    setOptionNotice(null);
                    onOpenWebDocument(slot);
                  }}
                >
                  <span className="cp-evaluation-plan-attachment-register-option-title">웹 문서 생성</span>
                  <span className="cp-evaluation-plan-attachment-register-option-desc">
                    웹에서 {slotLabel}을(를) 바로 작성합니다.
                  </span>
                </button>
                <button
                  type="button"
                  className={`cp-evaluation-plan-attachment-register-option${shareLinkDisabled ? ' is-disabled' : ' is-available'}`}
                  data-tutorial-ep="attach-option-share-link"
                  aria-disabled={shareLinkDisabled ? 'true' : undefined}
                  onClick={() => {
                    if (shareLinkDisabled) {
                      handleLockedOptionClick(
                        '웹 문서로 등록되어 있습니다. 공유링크 업로드로 변경하려면 등록을 삭제한 뒤 다시 등록해 주세요.'
                      );
                      return;
                    }
                    setOptionNotice(null);
                    onStepChange('share-link');
                  }}
                >
                  <span className="cp-evaluation-plan-attachment-register-option-title">공유링크 업로드</span>
                  <span className="cp-evaluation-plan-attachment-register-option-desc">
                    Google Drive 등 공유 링크를 등록합니다.
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="cp-evaluation-plan-attachment-register-lead" role="status">
                {slotLabel} 파일의 공유 링크를 입력해 주세요.
              </p>
              <label className="cp-evaluation-plan-field cp-evaluation-plan-attachment-register-field">
                <span>공유링크</span>
                <input
                  type="url"
                  className="cp-evaluation-plan-attachment-register-input"
                  value={shareUrlDraft}
                  onChange={(e) => {
                    setShareUrlDraft(e.target.value);
                    setShareUrlError(null);
                  }}
                  placeholder="https://..."
                  autoFocus
                  aria-invalid={shareUrlError ? 'true' : undefined}
                />
              </label>
              {shareUrlError ? (
                <p className="cp-evaluation-plan-attachment-register-error" role="alert">
                  {shareUrlError}
                </p>
              ) : null}
            </>
          )}
        </div>
        <div className="cp-modal-footer">
          {step === 'share-link' ? (
            <button
              type="button"
              className="cp-btn-secondary"
              onClick={() => {
                setShareUrlError(null);
                if (mode === 'edit') {
                  onClose();
                  return;
                }
                onStepChange('choose');
              }}
            >
              {mode === 'edit' ? '취소' : '이전'}
            </button>
          ) : (
            <button type="button" className="cp-btn-secondary" onClick={onClose}>
              취소
            </button>
          )}
          {step === 'share-link' ? (
            <button type="button" className="cp-btn-primary" onClick={handleSaveShareLink}>
              저장
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EvaluationPlanAttachmentRegisterModal;
